import {forwardRef, Ref} from 'react';
import './flat-starts-at-time.scss';
import {Box, FormControl, IconButton, InputAdornment, Slide} from '@mui/material';
import {MobileTimePicker, MobileTimePickerProps} from '@mui/x-date-pickers';
import moment from 'moment';
import FlatInput from '../flat-input/flat-input';
import {Pallete} from '../../theme/bluebird';
import {IoMdArrowDropdown} from 'react-icons/io';
import {MdClose} from 'react-icons/md';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';

export interface FlatStartsAtTimeProps extends MobileTimePickerProps<Date> {
  id?: string;
  label?: string;
  required?: boolean;
  readonly?: boolean;
  errors?: string;
  allowClear?: boolean;
  disableTooltip?: string;
}

export const FlatStartsAtTime = forwardRef(
  (
    {
      label,
      required,
      disabled,
      disableTooltip,
      readonly,
      errors,
      allowClear,
      ...rest
    }: FlatStartsAtTimeProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const {t} = useTranslation();
    const hasError = !!errors;

    return (
      <FormControl className="flat-starts-at-time" sx={{width: '100%', ...rest.sx}}>
        <MobileTimePicker
          {...rest}
          // @ts-ignore
          value={moment(rest.value)}
          disabled={disabled || readonly}
          slotProps={{
            toolbar: {
              // @ts-ignore
              toolbarTitle: t(translations.care_and_activities.handle_task_modal.select_time),
            },
          }}
          localeText={{
            cancelButtonLabel: t(translations.modal.cancel),
            okButtonLabel: t(translations.modal.confirm),
          }}
          slots={{
            textField: ({value, onChange, onBlur, onClick, inputProps}) => {
              return (
                <FlatInput
                  {...{
                    value,
                    onChange,
                    onBlur,
                    readonly,
                    disabled,
                    label,
                    required,
                  }}
                  disabledTooltip={disableTooltip}
                  ref={ref}
                  onClick={!readonly ? onClick : undefined}
                  inputProps={{
                    ...inputProps,
                    // @ts-ignore
                    value: rest.value ? moment(rest.value).format('LT') : '',
                  }}
                  InputProps={{
                    readOnly: readonly,
                    endAdornment: (
                      <Box
                        sx={{
                          display: 'flex',
                        }}
                      >
                        {allowClear && !readonly && !required && rest.value && (
                          <InputAdornment position="end">
                            <IconButton
                              component="div"
                              aria-label="calendar"
                              size="small"
                              onClick={(e) => {
                                // Prevent the click event from bubbling up
                                e.stopPropagation();
                                e.preventDefault();

                                // Clear the date
                                if (rest && rest.onChange) {
                                  rest.onChange(null, {
                                    validationError: null,
                                  });
                                }
                              }}
                            >
                              <MdClose />
                            </IconButton>
                          </InputAdornment>
                        )}
                        <InputAdornment position="end" disablePointerEvents={disabled}>
                          {!readonly && (
                            <IconButton
                              component="div"
                              aria-label="calendar"
                              size="small"
                              onClick={onClick}
                            >
                              <IoMdArrowDropdown />
                            </IconButton>
                          )}
                        </InputAdornment>
                      </Box>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      cursor: 'pointer !important',
                    },
                  }}
                />
              );
            },
          }}
        />
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
